import React from "react";
import { Button } from "@telegram-apps/telegram-ui";
import type { Upgrade } from "./upgrades/types";
import "./UpgradePickerModal.css";

interface UpgradePickerModalProps {
  readonly upgrades: ReadonlyArray<Upgrade>;
  readonly onPick: (upgrade: Upgrade) => void;
  readonly onCancel: () => void;
}

function describeUpgrade(u: Upgrade): string {
  const parts: string[] = [];

  // Main effect description
  const effectDesc = u.effects
    .map((e) => {
      switch (e.op) {
        case "profit_mult":
          return `${((e.value - 1) * 100).toFixed(0)}% profit boost`;
        case "profit_add_per_sec":
          return `+${(e.pct * 100).toFixed(2)}%/sec while holding`;
        case "loss_reduction_pct":
          return `${(e.pct * 100).toFixed(0)}% loss reduction`;
        case "loss_nullify":
          return "Loss nullified";
        case "trade_fee_pct":
          return `+${((e.buy ?? 0) * 100).toFixed(1)}%/${(
            (e.sell ?? 0) * 100
          ).toFixed(1)}% fees`;
        case "round_time_add_sec":
          return `+${e.value}s round time`;
        case "round_buys_add":
          return `+${e.value} buys`;
        case "leverage_mult":
          return `${e.value.toFixed(1)}x leverage`;
        case "auto_liquidate_at_drawdown_pct":
          return `Auto-liquidate at ${e.pct}% drawdown`;
        case "bank_mult":
          return `${e.value.toFixed(1)}x bank multiplier`;
        case "grant_life":
          return `+${e.value} life`;
        case "upgrade_random_card":
          return `Gain ${e.count} random upgrade`;
        case "bonus_holdings_pct":
          return `+${(e.pct * 100).toFixed(0)}% holdings bonus`;
        default:
          return u.id;
      }
    })
    .join(" • ");

  parts.push(effectDesc);

  // Trigger description
  const triggerDesc = getTriggerDescription(u.trigger);
  if (triggerDesc) parts.push(triggerDesc);

  // Condition description
  if (u.condition) {
    const conditionDesc = getConditionDescription(u);
    if (conditionDesc) parts.push(conditionDesc);
  }

  // Limitation description
  if (u.limits?.once_per) {
    parts.push(`Once per ${u.limits.once_per}`);
  }

  // Fee warning (if any fees)
  const hasFees = u.effects.some((e) => e.op === "trade_fee_pct");
  if (hasFees) {
    parts.push("⚠️ Adds trading fees");
  }

  // Stacking info
  if (u.stacking?.stack_mode) {
    const stackDesc = getStackingDescription(u.stacking);
    if (stackDesc) parts.push(stackDesc);
  }

  return parts.join(" • ");
}

function getTriggerDescription(trigger: string): string {
  switch (trigger) {
    case "first_win":
      return "First winning trade of each round";
    case "first_loss":
      return "First losing trade of each round";
    case "threshold_win_20":
      return "Winning trades ≥20% profit";
    case "threshold_win_30":
      return "Winning trades ≥30% profit";
    case "threshold_loss_10":
      return "Losses ≥10% of starting cash";
    case "trade_open":
      return "When opening a trade";
    case "round_start":
      return "At start of each round";
    case "trade_close_win":
      return "On winning trades";
    case "trade_close_loss":
      return "On losing trades";
    default:
      return "";
  }
}

function getConditionDescription(u: Upgrade): string {
  // For now, hardcode the known conditions
  switch (u.id) {
    case "diamond_5s_115":
      return "Only if held ≥5 seconds";
    case "scalper_2s_105":
      return "Only if held <2 seconds";
    default:
      return "";
  }
}

function getStackingDescription(stacking: any): string {
  if (stacking.stack_mode === "mult") {
    return "Stacks multiplicatively";
  }
  return "";
}

export function UpgradePickerModal({
  upgrades,
  onPick,
  onCancel,
}: UpgradePickerModalProps) {
  return (
    <div className="upgrade-overlay">
      <div className="upgrade-modal">
        <div className="upgrade-header">
          <h2 className="upgrade-title">Choose an Upgrade</h2>
          <div className="upgrade-subtitle">Pick 1 to apply for this round</div>
        </div>
        <div className="upgrade-cards">
          {upgrades.map((u) => (
            <div key={u.id} className="upgrade-card" onClick={() => onPick(u)}>
              <div className="upgrade-card-header">
                <div className="upgrade-card-id">{u.id}</div>
                <div className="upgrade-card-scope">
                  {u.scope.replace("_", " ")}
                </div>
              </div>

              <div className="upgrade-card-main-effect">
                {getMainEffectDescription(u)}
              </div>

              <div className="upgrade-card-details">
                <div className="upgrade-card-trigger">
                  <span className="detail-label">Trigger:</span>
                  <span className="detail-value">
                    {getTriggerDescription(u.trigger)}
                  </span>
                </div>

                {u.condition && (
                  <div className="upgrade-card-condition">
                    <span className="detail-label">Condition:</span>
                    <span className="detail-value">
                      {getConditionDescription(u)}
                    </span>
                  </div>
                )}

                {u.limits?.once_per && (
                  <div className="upgrade-card-limit">
                    <span className="detail-label">Limit:</span>
                    <span className="detail-value">
                      Once per {u.limits.once_per}
                    </span>
                  </div>
                )}

                {u.effects.some((e) => e.op === "trade_fee_pct") && (
                  <div className="upgrade-card-warning">
                    <span className="warning-icon">⚠️</span>
                    <span>Adds trading fees</span>
                  </div>
                )}

                {u.stacking?.stack_mode && (
                  <div className="upgrade-card-stacking">
                    <span className="detail-label">Stacking:</span>
                    <span className="detail-value">
                      {getStackingDescription(u.stacking)}
                    </span>
                  </div>
                )}
              </div>

              <Button
                size="s"
                className="pick-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPick(u);
                }}
              >
                Pick
              </Button>
            </div>
          ))}
        </div>
        <div className="upgrade-actions">
          <Button size="l" onClick={onCancel}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}

function getMainEffectDescription(u: Upgrade): string {
  return u.effects
    .filter((e) => e.op !== "trade_fee_pct") // Don't show fees in main effect
    .map((e) => {
      switch (e.op) {
        case "profit_mult":
          return `${((e.value - 1) * 100).toFixed(0)}% profit boost`;
        case "profit_add_per_sec":
          return `+${(e.pct * 100).toFixed(2)}%/sec while holding`;
        case "loss_reduction_pct":
          return `${(e.pct * 100).toFixed(0)}% loss reduction`;
        case "loss_nullify":
          return "Loss nullified";
        case "round_time_add_sec":
          return `+${e.value}s round time`;
        case "round_buys_add":
          return `+${e.value} buys`;
        case "leverage_mult":
          return `${e.value.toFixed(1)}x leverage`;
        case "auto_liquidate_at_drawdown_pct":
          return `Auto-liquidate at ${e.pct}% drawdown`;
        case "bank_mult":
          return `${e.value.toFixed(1)}x bank multiplier`;
        case "grant_life":
          return `+${e.value} life`;
        case "upgrade_random_card":
          return `Gain ${e.count} random upgrade`;
        case "bonus_holdings_pct":
          return `+${(e.pct * 100).toFixed(0)}% holdings bonus`;
        default:
          return u.id;
      }
    })
    .join(" • ");
}
